/* Velara full search results — the Clerk omni-search layout on Shopify's own search.
 *
 * predictive-search.js still owns the overlay (open/close, the input, debouncing) and hands every
 * query of 2+ characters to VelaraOmni.search(). From there this file renders
 * sections/search-omni (Section Rendering API on /search) into the overlay's results box and keeps
 * it live:
 *
 *   · a NEW QUERY replaces the whole panel — filters start clear, as they did in Clerk;
 *   · a FILTER, SORT or CHIP replaces only the results (count, grid, "load more") and rewrites the
 *     facet counts and checked flags IN PLACE, so open groups and focus survive. Same reasoning as
 *     collection-filter-ajax.js, which cannot be reused here: it is bound to the PLP section and
 *     rewrites the page URL;
 *   · "LOAD MORE" appends the next page of cards;
 *   · the CATEGORIES and PAGES column comes from predictive search, because /search never returns
 *     collections.
 *
 * Every request carries a sequence number, so a slow earlier reply can never overwrite a newer one.
 */
(function () {
  'use strict';

  var SECTION = 'search-omni';
  var FORM_ID = 'VelaraOmniFacets';
  var seq = 0;
  var terms = '';

  var routeRoot = function () { return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/'; };
  var box = function () { return document.querySelector('#VelaraSearchDrawer [data-search-results]'); };
  var omni = function () { var b = box(); return b && b.querySelector('[data-omni-root]'); };

  function searchUrl(q, params) {
    var u = new URL(routeRoot() + 'search', window.location.origin);
    u.searchParams.set('q', q);
    u.searchParams.set('type', 'product');
    u.searchParams.set('options[prefix]', 'last');
    (params || []).forEach(function (p) { u.searchParams.append(p[0], p[1]); });
    u.searchParams.set('section_id', SECTION);
    return u.toString();
  }

  // An href Liquid rendered (a chip's url_to_remove, "clear", the next page) pointed at the section.
  function asSection(href) {
    var u = new URL(href, window.location.origin);
    u.searchParams.set('section_id', SECTION);
    return u.toString();
  }

  function fetchDoc(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error(r.status);
      return r.text();
    }).then(function (html) { return new DOMParser().parseFromString(html, 'text/html'); });
  }

  function busy(on) {
    var b = box();
    if (!b) return;
    if (on) b.setAttribute('aria-busy', 'true'); else b.removeAttribute('aria-busy');
  }

  function announce() {
    var o = omni();
    var count = o && o.querySelector('[data-omni-count]');
    var status = document.querySelector('[data-search-status]');
    if (!count || !status || !window.VelaraSearch || !VelaraSearch.announce) return;
    VelaraSearch.announce((count.dataset.count + ' ' + (status.dataset.label || '')).trim());
  }

  /* ---------- a new query ---------- */

  function search(q, target) {
    var mine = ++seq;
    terms = q;
    busy(true);
    if (rankSource) rankOrder();   // start loading the ranking collection's order now
    fetchDoc(searchUrl(q)).then(function (doc) {
      if (mine !== seq) return;
      var fresh = doc.querySelector('[data-omni-root]');
      target.innerHTML = fresh ? fresh.outerHTML : '';
      holdForRanking();
      busy(false);
      announce();
      fillLists(q, mine);
      if (rankSource) applyRanking(mine);
    }).catch(function () {
      if (mine !== seq) return;
      target.innerHTML = '';
      busy(false);
    });
  }

  function reset() {
    ++seq;
    terms = '';
  }

  /* ---------- before typing: the browse collection ---------- */

  // Rendered once per page and kept: reopening search, or clearing the field, shows it instantly.
  // A filtered/sorted browse state is not cached — only the untouched first page is.
  var browseCache = null;

  function browse(path, target) {
    var mine = ++seq;
    terms = '';
    loadCategoryIndex();   // ready before the first query
    // With a ranking collection, "before typing" IS that collection: its own order is the ranking,
    // natively, with no API sorting. (The ranked typed search needs the order prefetched too.)
    if (rankSource) {
      path = routeRoot() + 'collections/' + encodeURIComponent(rankSource);
      rankOrder();
    }
    if (browseCache && browseCache.path === path) {
      target.innerHTML = browseCache.html;
      capLists(target.querySelector('[data-omni-lists]'));
      busy(false);
      announce();
      return;
    }
    busy(true);
    fetchDoc(asSection(path)).then(function (doc) {
      var fresh = doc.querySelector('[data-omni-root]');
      if (fresh) browseCache = { path: path, html: fresh.outerHTML };
      if (mine !== seq) return;
      target.innerHTML = fresh ? fresh.outerHTML : '';
      capLists(target.querySelector('[data-omni-lists]'));
      busy(false);
      announce();
    }).catch(function () {
      if (mine === seq) { target.innerHTML = ''; busy(false); }
    });
  }

  /* ---------- filter / sort / chip ---------- */

  // Filters and sort are appended to the panel's own base: the search URL for a query, the
  // collection URL before typing.
  function stateUrl() {
    var o = omni();
    var base = (o && o.getAttribute('data-omni-base')) || '';
    var u = new URL(base || (routeRoot() + 'search'), window.location.origin);
    stateParams().forEach(function (p) { u.searchParams.append(p[0], p[1]); });
    u.searchParams.set('section_id', SECTION);
    return u.toString();
  }

  function stateParams() {
    var o = omni();
    var out = [];
    if (!o) return out;
    var form = document.getElementById(FORM_ID);
    if (form) new FormData(form).forEach(function (v, k) { if (String(v).length) out.push([k, v]); });
    var sort = o.querySelector('[data-omni-sort]');
    if (sort && sort.value) out.push(['sort_by', sort.value]);
    return out;
  }

  function refine(url) {
    var o = omni();
    if (!o) return;
    var mine = ++seq;
    busy(true);
    fetchDoc(url).then(function (doc) {
      if (mine !== seq) return;
      var fresh = doc.querySelector('[data-omni-root]');
      var live = omni();
      if (!fresh || !live) return;
      swap(live, fresh, '[data-omni-results]');
      swap(live, fresh, '[data-omni-count]');
      syncSort(live, fresh);
      reconcileFacets(live, fresh);
      holdForRanking();
      busy(false);
      announce();
      if (rankSource) applyRanking(mine);
    }).catch(function () {
      if (mine === seq) busy(false);
    });
  }

  function swap(live, fresh, sel) {
    var a = live.querySelector(sel);
    var b = fresh.querySelector(sel);
    if (a && b) a.replaceWith(document.importNode(b, true));
  }

  function syncSort(live, fresh) {
    var a = live.querySelector('[data-omni-sort]');
    var b = fresh.querySelector('[data-omni-sort]');
    if (a && b && document.activeElement !== a) a.value = b.value;
  }

  // Counts and checked flags are written onto the existing inputs; the <details> and the focused
  // control are never re-created. The active-filter chips and the clear count are plain output,
  // so those are replaced (or added / removed) wholesale.
  function reconcileFacets(live, fresh) {
    var freshByKey = {};
    fresh.querySelectorAll('#' + FORM_ID + ' input').forEach(function (i) {
      freshByKey[i.name + ' ' + (i.type === 'checkbox' ? i.value : '')] = i;
    });
    live.querySelectorAll('#' + FORM_ID + ' input').forEach(function (i) {
      var f = freshByKey[i.name + ' ' + (i.type === 'checkbox' ? i.value : '')];
      if (!f) return;
      if (i.type === 'checkbox') {
        i.checked = f.checked;
        var ls = i.closest('label') && i.closest('label').querySelector('span');
        var fs = f.closest('label') && f.closest('label').querySelector('span');
        if (ls && fs) ls.textContent = fs.textContent;
        var label = i.closest('label');
        if (label) label.classList.toggle('is-disabled', f.closest('label').classList.contains('is-disabled'));
      } else if (document.activeElement !== i) {
        i.value = f.value;
      }
    });

    var panel = live.querySelector('.velara-collection__filters');
    var freshPanel = fresh.querySelector('.velara-collection__filters');
    if (panel && freshPanel) {
      var chips = panel.querySelector('.velara-collection__active');
      var freshChips = freshPanel.querySelector('.velara-collection__active');
      if (chips && !freshChips) chips.remove();
      else if (chips && freshChips) chips.replaceWith(document.importNode(freshChips, true));
      else if (!chips && freshChips) {
        var form = panel.querySelector('#' + FORM_ID);
        if (form) form.insertAdjacentElement('beforebegin', document.importNode(freshChips, true));
      }
      swap(panel, freshPanel, '.velara-collection__filters-clear');
    }
  }

  /* ---------- load more ---------- */

  function loadMore(btn) {
    var next = btn.getAttribute('data-next-url');
    if (!next || btn.disabled) return;
    btn.disabled = true;
    var mine = seq; // a filter or new query since then makes this page irrelevant
    fetchDoc(asSection(next)).then(function (doc) {
      if (mine !== seq) return;
      var grid = omni() && omni().querySelector('[data-omni-grid]');
      var freshGrid = doc.querySelector('[data-omni-grid]');
      if (grid && freshGrid) {
        Array.prototype.forEach.call(freshGrid.children, function (c) { grid.appendChild(document.importNode(c, true)); });
      }
      var more = btn.closest('.velara-omni__more');
      var freshMore = doc.querySelector('.velara-omni__more');
      if (more && freshMore) more.replaceWith(document.importNode(freshMore, true));
      else if (more) more.remove();
    }).catch(function () { btn.disabled = false; });
  }

  /* ---------- categories + pages ---------- */

  /* ---------- category names (sections/search-category-index) ----------
   * Every published, searchable collection with its shopper-facing name in this language, loaded
   * once per visit (and kept 30 min in the tab). Matching is per word: each word of the query must
   * start a word of the name, accents and case ignored. */
  var catIndex = null, catIndexPromise = null;
  function norm(s) { return String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase(); }
  function loadCategoryIndex() {
    if (catIndexPromise) return catIndexPromise;
    var lang = (document.documentElement.lang || '').slice(0, 2);
    var key = 'velaraCatIndex:' + routeRoot() + lang;
    try {
      var c = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (c && Date.now() - c.at < 30 * 60 * 1000) { catIndex = c.list; catIndexPromise = Promise.resolve(catIndex); return catIndexPromise; }
    } catch (e) { /* no cache */ }
    var page = function (n) {
      return fetchDoc(routeRoot() + 'collections?section_id=search-category-index&page=' + n).then(function (doc) {
        var el = doc.querySelector('[data-search-category-index]');
        return { pages: el ? parseInt(el.getAttribute('data-pages'), 10) || 1 : 1, list: el ? JSON.parse(el.textContent) : [] };
      });
    };
    catIndexPromise = page(1).then(function (first) {
      var rest = [];
      for (var n = 2; n <= first.pages; n++) rest.push(page(n));
      return Promise.all(rest).then(function (more) {
        var list = first.list.concat.apply(first.list, more.map(function (m) { return m.list; }));
        list.forEach(function (e) { e.n = norm(e.t).split(/[^a-z0-9]+/).filter(Boolean); });
        catIndex = list;
        try { sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), list: list })); } catch (e) { /* full */ }
        return list;
      });
    });
    catIndexPromise.catch(function () { catIndexPromise = null; });
    return catIndexPromise;
  }
  function categoryMatches(q, already) {
    if (!catIndex) return [];
    var words = norm(q).split(/[^a-z0-9]+/).filter(Boolean);
    if (!words.length) return [];
    var have = {};
    already.forEach(function (e) {
      var m = (e.href || '').match(/\/collections\/([^/?#]+)/);
      if (m) have[m[1]] = true;
    });
    return catIndex
      .filter(function (e) {
        return !have[e.h] && words.every(function (w) { return e.n.some(function (n) { return n.indexOf(w) === 0; }); });
      })
      .sort(function (a, b) {   // names that START with the query first, then the shorter ones
        var as = a.n[0].indexOf(words[0]) === 0 ? 0 : 1, bs = b.n[0].indexOf(words[0]) === 0 ? 0 : 1;
        return as - bs || a.t.length - b.t.length;
      })
      .map(function (e) { return { label: e.t, href: e.u }; });
  }

  function fillLists(q, mine) {
    loadCategoryIndex();
    var url = routeRoot() + 'search/suggest?q=' + encodeURIComponent(q) +
      // limit_scope=each: 10 of EACH type — the default cap is shared, so 10 categories left no room
      // for a single page or article.
      '&resources[type]=collection,page,article&resources[limit]=10&resources[limit_scope]=each' +
      '&section_id=predictive-search';
    Promise.all([fetchDoc(url), loadCategoryIndex().catch(function () { return null; })]).then(function (both) {
      var doc = both[0];
      if (mine !== seq && terms !== q) return;
      var holder = omni() && omni().querySelector('[data-omni-lists]');
      if (!holder) return;
      holder.innerHTML = '';
      // Both lists are always built: on desktop an empty one is hidden (Clerk printed no title for
      // it), on phones it is the content of its tab and says there is nothing.
      [['[data-predict-collections]', 'categories'], ['[data-predict-pages]', 'pages']].forEach(function (pair) {
        var group = doc.querySelector(pair[0]);
        var links = group ? group.querySelectorAll('a[href]') : [];
        var list = document.createElement('div');
        list.className = 'velara-omni__list velara-omni__list--' + pair[1] + (links.length ? '' : ' is-empty');
        var title = document.createElement('p');
        title.className = 'velara-omni__list-title';
        var tab = omni() && omni().querySelector('[data-omni-tab-btn="' + pair[1] + '"]');
        title.textContent = tab ? tab.textContent.trim() : '';
        list.appendChild(title);
        if (!links.length) {
          var none = document.createElement('p');
          none.className = 'velara-omni__list-empty';
          none.textContent = holder.getAttribute('data-empty-label') || '';
          list.appendChild(none);
        }
        var entries = Array.prototype.map.call(links, function (src) {
          return { label: src.textContent.trim(), href: src.getAttribute('href') };
        });
        // Categories: Shopify's matches first, then collections whose PUBLIC name matches — the
        // only way "lui" finds "Per lui" when the collection is titled "For him".
        if (pair[1] === 'categories') entries = entries.concat(categoryMatches(q, entries));
        if (entries.length && list.classList.contains('is-empty')) {
          list.classList.remove('is-empty');
          var stale = list.querySelector('.velara-omni__list-empty');
          if (stale) stale.remove();
        }
        var seen = {};
        entries.forEach(function (e) {
          var key = e.label.toLowerCase();
          if (seen[key]) return;   // two collections with one public name read as a duplicate
          seen[key] = true;
          var a = document.createElement('a');
          a.className = 'velara-omni__list-item';
          a.href = e.href;
          a.textContent = e.label;
          list.appendChild(a);
        });
        holder.appendChild(list);
      });
      capLists(holder);
    }).catch(function () { /* the column just stays empty */ });
  }

  // A short query ("co") matches dozens of categories and pages; beyond LIST_CAP a list shows
  // "Mostra tutti (n)" instead of pushing the results down. Phones list everything: there the list
  // is a whole tab of its own (CSS).
  var LIST_CAP = 6;
  function capLists(holder) {
    if (!holder) return;
    var label = holder.getAttribute('data-more-label') || '+';
    holder.querySelectorAll('.velara-omni__list').forEach(function (list) {
      var items = list.querySelectorAll('.velara-omni__list-item');
      if (items.length <= LIST_CAP || list.querySelector('.velara-omni__list-more')) return;
      for (var i = LIST_CAP; i < items.length; i++) items[i].classList.add('is-extra');
      var more = document.createElement('button');
      more.type = 'button';
      more.className = 'velara-omni__list-more';
      more.setAttribute('data-omni-list-more', '');
      more.textContent = label + ' (' + items.length + ')';
      list.appendChild(more);
    });
  }

  /* ---------- wiring (delegated: the panel is re-rendered on every query) ---------- */

  function inOmni(el) { return el && el.closest && el.closest('#VelaraSearchDrawer [data-omni-root]'); }

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!inOmni(t)) return;
    if (t.closest('#' + FORM_ID) || t.matches('[data-omni-sort]')) {
      refine(stateUrl());
    }
  });

  document.addEventListener('submit', function (e) {
    if (e.target && e.target.id === FORM_ID) {
      e.preventDefault();
      refine(stateUrl());
    }
  });

  /* ---------- phones: tabs, filter sheet, back to top ---------- */

  var panel = function () { return document.querySelector('#VelaraSearchDrawer .velara-search-drawer__panel'); };

  function showTab(name) {
    var o = omni();
    if (!o) return;
    o.setAttribute('data-omni-tab', name);
    o.querySelectorAll('[data-omni-tab-btn]').forEach(function (b) {
      b.setAttribute('aria-selected', b.getAttribute('data-omni-tab-btn') === name ? 'true' : 'false');
    });
    var p = panel();
    if (p) p.scrollTop = 0;
  }

  function filters(open) {
    var o = omni();
    if (!o) return;
    o.classList.toggle('is-filters-open', open);
    var btn = o.querySelector('[data-omni-filters-open]');
    if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) {
      var close = o.querySelector('#VelaraOmniFilters [data-filters-close]');
      if (close) close.focus();
    } else if (btn) {
      btn.focus();
    }
  }

  // Esc closes the sheet first; a second Esc (predictive-search.js) closes the overlay.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var o = omni();
    if (o && o.classList.contains('is-filters-open')) {
      e.stopImmediatePropagation();
      filters(false);
    }
  }, true);

  document.addEventListener('click', function (e) {
    if (!inOmni(e.target)) return;
    var more = e.target.closest('[data-omni-more]');
    if (more) { loadMore(more); return; }
    var rankedMore = e.target.closest('[data-omni-ranked-more]');
    if (rankedMore) { loadRankedMore(rankedMore); return; }
    var listMore = e.target.closest('[data-omni-list-more]');
    if (listMore) {
      var list = listMore.closest('.velara-omni__list');
      if (list) list.classList.add('is-expanded');
      listMore.remove();
      return;
    }
    var tab = e.target.closest('[data-omni-tab-btn]');
    if (tab) { showTab(tab.getAttribute('data-omni-tab-btn')); return; }
    if (e.target.closest('[data-omni-filters-open]')) { filters(true); return; }
    if (e.target.closest('[data-filters-close]')) { filters(false); return; }
    if (e.target.closest('[data-omni-top]')) {
      var p = panel();
      if (p) p.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    // Active-filter chips and "clear" are real links (they work with JS off); keep that, but stay
    // in the overlay for a plain click.
    var link = e.target.closest('.velara-chip, .velara-collection__filters-clear');
    if (!link || link.tagName !== 'A') return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    refine(asSection(link.getAttribute('href')));
  });

  /* ---------- RANKED SEARCH (a collection's order is the ranking) ----------
   * Shopify's search only sorts by relevance or price. Here the ranking is a COLLECTION's own order
   * (theme setting "Ranking collection", or ?search_rank=<handle> to try one; ?search_rank=off):
   * a product's position in that collection is its rank, and merchandising sets the ranking by
   * ordering that collection — in Shopify admin or from VPIM.
   * Per query, every matching product id comes from the Storefront API (tokenless, 250 a call,
   * same filters as the panel), is sorted by how many typed words its title has (whole words, or
   * the same word with another ending or one typo; the last typed word also as the start of one;
   * 1–2 letter fillers like in/di/of skipped), exact words ahead of near ones, then by that
   * position — products outside the collection follow, in Shopify's relevance order — and cards render 20 at a time in that order through this same
   * section (an `id:a OR id:b …` search, so they stay the theme's own cards). Shopify's
   * order is kept hidden until ours is ready, so nothing visibly reshuffles.
   * ⚠️ By ID, never by handle: the API answers in the page's language, so off the primary language
   * it returns TRANSLATED handles, and /search's `handle:` only matches the primary-language one —
   * every card came back missing on /es and /en (2026-10-26: "283 resultados", empty grid).
   * ⚠️ Title words first: the ranking collection holds the whole catalogue, so on its own it buried
   * the product searched BY NAME under everything matching one word ("anello chevalier cuore in oro":
   * Shopify's #1, our #436 — 2026-11-03). A one-word query ties every title, so it still gets the
   * collection's order untouched.
   * Typed search with the relevance sort only; price sorts keep Shopify's order. Any failure
   * shows Shopify's order. */
  var RANK_KEY = 'velaraSearchRank';
  var rankFromUrl = false;
  var rankSource = (function () {
    var fromSetting = (document.getElementById('VelaraSearchDrawer') || { dataset: {} }).dataset.searchRank || null;
    try {
      var p = new URLSearchParams(window.location.search).get('search_rank');
      if (p === 'off') { sessionStorage.setItem(RANK_KEY, 'off'); return null; }
      if (p) { sessionStorage.setItem(RANK_KEY, p); rankFromUrl = true; return p; }
      var stored = sessionStorage.getItem(RANK_KEY);
      if (stored === 'off') return null;
      if (stored) { rankFromUrl = true; return stored; }
    } catch (e) { /* storage blocked: the setting still applies */ }
    return fromSetting;
  })();
  var ranked = null; // { items: [{ id, h }], shown: n } — h is the handle in the page's language
  var filterInputs = {}; // "param\u0000label" -> Storefront API filter input (JSON string)

  // The ranking collection's order as { productId: position }, loaded once per tab (15 min).
  var RANK_TTL = 15 * 60 * 1000;
  var rankOrderPromise = null;
  function rankOrder() {
    if (rankOrderPromise) return rankOrderPromise;
    var cacheKey = 'velaraRankOrder:' + rankSource;
    try {
      var c = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
      if (c && Date.now() - c.at < RANK_TTL) {
        var m = {};
        c.ids.forEach(function (id, i) { m[id] = i; });
        rankOrderPromise = Promise.resolve(m);
        return rankOrderPromise;
      }
    } catch (e) { /* no cache */ }
    rankOrderPromise = (async function () {
      var ids = [], after = null;
      do {
        var d = await storefrontQuery(
          'query($h: String!, $after: String) { collection(handle: $h) { products(first: 250, after: $after, sortKey: COLLECTION_DEFAULT) { pageInfo { hasNextPage endCursor } nodes { id } } } }',
          { h: rankSource, after: after });
        if (!d.collection) throw new Error('no ranking collection');
        d.collection.products.nodes.forEach(function (n) { ids.push(n.id); });
        after = d.collection.products.pageInfo.hasNextPage ? d.collection.products.pageInfo.endCursor : null;
      } while (after);
      try { sessionStorage.setItem(cacheKey, JSON.stringify({ at: Date.now(), ids: ids })); } catch (e) { /* full */ }
      var m = {};
      ids.forEach(function (id, i) { m[id] = i; });
      return m;
    })();
    rankOrderPromise.catch(function () { rankOrderPromise = null; });  // retry on the next query
    return rankOrderPromise;
  }

  // Keep Shopify's own order off screen while ours loads (only where ranking will apply).
  function holdForRanking() {
    var o = omni();
    if (!o || !rankSource || o.getAttribute('data-omni-mode') !== 'search') return;
    var sort = o.querySelector('[data-omni-sort]');
    if (sort && sort.value && sort.value !== 'relevance') return;
    o.classList.add('is-ranking');
  }
  function releaseRanking() {
    var o = omni();
    if (o) o.classList.remove('is-ranking');
  }

  function storefrontQuery(query, variables) {
    return fetch('/api/2025-01/graphql.json', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: query, variables: variables })
    }).then(function (r) { return r.json(); }).then(function (j) {
      if (j.errors || !j.data) throw new Error('storefront');
      return j.data;
    });
  }

  var SF_QUERY = 'query($q: String!, $after: String, $f: [ProductFilter!]) @inContext(language: ' +
    ((document.documentElement.lang || 'it').slice(0, 2).toUpperCase()) + ') {' +
    ' search(query: $q, first: 250, after: $after, types: PRODUCT, prefix: LAST, productFilters: $f) {' +
    ' totalCount pageInfo { hasNextPage endCursor } edges { node { ... on Product { id handle title } } }' +
    ' productFilters { id values { label input } } } }';

  function storefront(variables) {
    return storefrontQuery(SF_QUERY, variables).then(function (d) { return d.search; });
  }

  async function allMatches(q, filters) {
    var items = [], after = null, total = 0;
    do {
      var s = await storefront({ q: q, after: after, f: filters });
      total = s.totalCount;
      s.productFilters.forEach(function (f) {
        f.values.forEach(function (v) { filterInputs[f.id + '\u0000' + v.label] = v.input; });
      });
      s.edges.forEach(function (e) { if (e.node && e.node.handle) items.push(e.node); });
      after = s.pageInfo.hasNextPage ? s.pageInfo.endCursor : null;
    } while (after);
    return { items: items, total: total };
  }

  // The panel's ticked filters as Storefront API inputs: matched by the filter's param name and the
  // value's label (the label minus its "(n)" count). null = something could not be mapped.
  function apiFilters() {
    var form = document.getElementById(FORM_ID);
    if (!form) return [];
    var out = [], unmapped = false;
    form.querySelectorAll('input[type="checkbox"]:checked').forEach(function (i) {
      var span = i.closest('label') && i.closest('label').querySelector('span');
      var label = span ? span.textContent.replace(/\s*\(\d+\)\s*$/, '').trim() : i.value;
      var input = filterInputs[i.name + '\u0000' + label];
      if (input) out.push(JSON.parse(input)); else unmapped = true;
    });
    var min = form.querySelector('input[name="filter.v.price.gte"]');
    var max = form.querySelector('input[name="filter.v.price.lte"]');
    if ((min && min.value) || (max && max.value)) {
      var price = {};
      if (min && min.value) price.min = parseFloat(min.value);
      if (max && max.value) price.max = parseFloat(max.value);
      out.push({ price: price });
    }
    return unmapped ? null : out;
  }

  // Lowercase, accent-free words, so a query and a title compare in any of the shop's languages.
  function words(s) {
    return (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').match(/[a-z0-9]+/g) || [];
  }
  // The typed words that count: 1–2 letter fillers (in, di, de, of…) drop out, except the last word,
  // which may still be being typed.
  function typedWords(q) {
    var w = words(q);
    return w.filter(function (x, i) { return (x.length > 2 || i === w.length - 1) && w.indexOf(x) === i; });
  }
  // Two words count as the same when only the ending differs (anelli/anello, anillos/anillo,
  // collane/collana) or, from 6 letters, by one typo (chevlier/chevalier) — Shopify's own matching
  // forgives both, so the grouping has to as well.
  function sameWord(q, w) {
    var min = Math.min(q.length, w.length), n = 0;
    while (n < min && q[n] === w[n]) n++;
    if (min >= 4 && Math.abs(q.length - w.length) <= 2 && n >= Math.max(4, min - 1)) return true;
    return min >= 6 && oneEdit(q, w);
  }
  function oneEdit(a, b) {
    if (Math.abs(a.length - b.length) > 1) return false;
    var i = 0, j = 0, e = 0;
    while (i < a.length && j < b.length) {
      if (a[i] === b[j]) { i++; j++; continue; }
      if (++e > 1) return false;
      if (a.length > b.length) i++; else if (b.length > a.length) j++; else { i++; j++; }
    }
    return e + (a.length - i) + (b.length - j) <= 1;
  }
  // How many typed words the title has: `exact` as whole words, `near` also counting sameWord; the
  // last typed word also matches the start of a word ("chev" finds CHEVALIER, "in" not INIZIALE).
  // Sorting by near, then exact, keeps an exact-name search exactly where whole words put it.
  function titleTier(title, typed) {
    var tw = words(title), last = typed.length - 1, near = 0, exact = 0;
    typed.forEach(function (q, i) {
      if (tw.some(function (w) { return w === q || (i === last && w.indexOf(q) === 0); })) { exact++; near++; }
      else if (tw.some(function (w) { return sameWord(q, w); })) near++;
    });
    return { near: near, exact: exact };
  }

  function handleOf(cell) {
    var a = cell.querySelector('a[href*="/products/"]');
    var m = a && a.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  // Cards for up to 20 products, appended in OUR order (the search returns them in its own).
  // Searched by numeric id (language-independent); matched back by the handle the card links to,
  // which is the same translated handle the API gave us.
  async function renderRanked(start, grid, mine) {
    var slice = ranked.items.slice(start, start + 20);
    if (!slice.length) return 0;
    var url = routeRoot() + 'search?type=product&section_id=' + SECTION + '&q=' +
      encodeURIComponent(slice.map(function (x) { return 'id:' + x.id.split('/').pop(); }).join(' OR '));
    var doc = await fetchDoc(url);
    if (mine !== seq) return -1;
    var byHandle = {};
    doc.querySelectorAll('[data-omni-grid] > .velara-omni__cell').forEach(function (c) {
      var h = handleOf(c);
      if (h) byHandle[h] = c;
    });
    slice.forEach(function (x) { if (byHandle[x.h]) grid.appendChild(document.importNode(byHandle[x.h], true)); });
    return slice.length;
  }

  function rankedMoreButton(o) {
    var wrap = o.querySelector('.velara-omni__more');
    var remaining = ranked.items.length - ranked.shown;
    if (remaining <= 0) { if (wrap) wrap.remove(); return; }
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'velara-omni__more';
      wrap.innerHTML = '<button type="button" class="velara-omni__more-btn"></button>';
      o.querySelector('[data-omni-results]').appendChild(wrap);
    }
    var btn = wrap.querySelector('button');
    btn.removeAttribute('data-omni-more');
    btn.removeAttribute('data-next-url');
    btn.setAttribute('data-omni-ranked-more', '');
    btn.disabled = false;
    if (!btn.textContent.trim()) btn.textContent = '+';
  }

  async function applyRanking(mine) {
    var o = omni();
    if (!o || o.getAttribute('data-omni-mode') !== 'search') return;
    var sort = o.querySelector('[data-omni-sort]');
    if (sort && sort.value && sort.value !== 'relevance') { ranked = null; releaseRanking(); return; }
    var grid = o.querySelector('[data-omni-grid]');
    if (!grid) { releaseRanking(); return; }
    busy(true);
    try {
      var filters = apiFilters();
      if (filters === null) { await allMatches(terms, []); filters = apiFilters(); }  // learn the inputs once
      if (filters === null) throw new Error('unmapped filter');
      var both = await Promise.all([allMatches(terms, filters), rankOrder()]);
      var res = both[0], order = both[1];
      if (mine !== seq) return;
      var OUTSIDE = 1e9;   // not in the ranking collection: after it, in Shopify's relevance order
      var typed = typedWords(terms);
      ranked = {
        shown: 0,
        items: res.items
          .map(function (n, i) {
            var t = titleTier(n.title, typed);
            return { id: n.id, h: n.handle, t: t.near, x: t.exact, r: n.id in order ? order[n.id] : OUTSIDE + i };
          })
          .sort(function (a, b) { return b.t - a.t || b.x - a.x || a.r - b.r; })
      };
      var fresh = document.createElement('div');
      var n = await renderRanked(0, fresh, mine);
      if (n < 0 || mine !== seq) return;
      // matches but no card came back: keep Shopify's grid rather than empty it
      if (n > 0 && !fresh.children.length) throw new Error('ranked cards missing');
      grid.innerHTML = '';
      Array.prototype.slice.call(fresh.children).forEach(function (c) { grid.appendChild(c); });
      ranked.shown = n;
      var count = o.querySelector('[data-omni-count]');
      if (count) {
        // past 1000 matches the API's totalCount is 0: keep the count Shopify rendered
        if (res.total) {
          count.setAttribute('data-count', res.total);
          var strong = count.querySelector('strong');
          if (strong) strong.textContent = res.total;
        }
        // a ranking tried from the URL says so; the configured one is silent
        if (rankFromUrl && !count.querySelector('.velara-omni__rank-flag')) {
          var flag = document.createElement('span');
          flag.className = 'velara-omni__rank-flag';
          flag.textContent = ' · ordine: ' + rankSource;
          count.appendChild(flag);
        }
      }
      rankedMoreButton(o);
      announce();
    } catch (e) {
      ranked = null;   // Shopify's own order stays
    } finally {
      if (mine === seq) { busy(false); releaseRanking(); }
    }
  }

  async function loadRankedMore(btn) {
    var o = omni();
    var grid = o && o.querySelector('[data-omni-grid]');
    if (!ranked || !grid || btn.disabled) return;
    btn.disabled = true;
    var mine = seq;
    try {
      var n = await renderRanked(ranked.shown, grid, mine);
      if (n < 0) return;
      ranked.shown += n;
      rankedMoreButton(o);
    } catch (e) { btn.disabled = false; }
  }

  window.VelaraOmni = { search: search, reset: reset, browse: browse };
})();
